// Libs
import React from 'react';
import { Route, Switch, Redirect, withRouter } from 'react-router';
import { Container } from 'react-bootstrap';
import moment from 'moment';

// Services & Helpers
import AccountService     from 'services/AccountService';
import GlobalStateService from 'services/GlobalStateService';

// Components
import Layout             from 'components/layout/Layout';
import LoginPage          from 'components/login/LoginPage';
import LoginByGUID        from 'components/login/LoginByGUID';
import PasswordResetPage  from 'components/login/PasswordResetPage';
import Loader             from 'components/common/Loader';
import InstructorListPage from 'components/instructor/InstructorListPage';
import InstructorViewPage from 'components/instructor/InstructorViewPage';
import AvailabilitySelectorPage
                          from 'components/instructor/AvailabilitySelectorPage';
import BookingsPage       from 'components/bookings/BookingsPage';
import CalendarPage       from 'components/calendar/CalendarPage';
import EnablementPage     from 'components/enablement/EnablementPage';
import ReportsPage        from 'components/report/ReportsPage';
import MapPage            from 'components/map/MapPage';
import TodosPage          from 'components/todos/TodosPage';
import InvoicesPage       from 'components/invoicing/InvoicesPage';
import InstructorInvoicesPage
                          from 'components/invoicing/InstructorInvoicesPage';
import BillsPage          from 'components/invoicing/BillsPage';
import HelpPage           from 'components/help/HelpPage';
import DashboardPage      from 'components/dashboard/DashboardPage';

import EnquiriesPage      from 'components/admin/enquiries/EnquiriesPage';
import CourseCheckerPage  from 'components/admin/course-checker/CourseCheckerPage';
import JobRequestsPage    from 'components/admin/job-request/JobRequestsPage';
import CoursesPage        from 'components/admin/courses/CoursesPage';
import EmailTypesPage     from 'components/admin/email-types/EmailTypesPage';
import UsersPage          from 'components/admin/users/UsersPage';
import CertsPage          from 'components/admin/certs/CertsPage';
import ImportPage         from 'components/admin/import/ImportPage';
import ReferralCodesPage  from 'components/admin/referral-codes/ReferralCodesPage';
import UnbilledDaysPage   from 'components/admin/unbilled-day/UnbilledDaysPage';
import EnablementComparePage
                          from 'components/admin/enablement-compare/EnablementComparePage';
import OnboardingPage     from 'components/onboarding/OnboardingPage';
import SignContractPage   from 'components/instructor/SignContractPage';
import PartnersPage       from 'components/admin/partners/PartnersPage';
import CostCentresPage    from 'components/admin/cost-centres/CostCentresPage';
import SubAgencyListPage  from 'components/admin/sub-agencies/SubAgencyListPage';
import SubAgencyViewPage  from 'components/admin/sub-agencies/SubAgencyViewPage';
import ImportedBookingsPage
                          from 'components/admin/imported-bookings/ImportedBookingsPage';
import ContactsPage       from 'components/admin/contacts/ContactsPage';
import ContractsPage      from 'components/admin/contract/ContractsPage';
import DevTasksPage       from 'components/admin/dev-tasks/DevTasksPage';
import CountriesPage      from 'components/admin/countries/CountriesPage';
import HelpArticlesPage   from 'components/admin/help/HelpArticlesPage';
import TagsPage           from 'components/admin/tags/TagsPage';

window.moment = moment;

//-------------------------------------------------------------------------------------------------------------------

class App extends React.Component {

    constructor(props) {
        super(props);

        this.checkLogin = this.checkLogin.bind(this);
        this.switchInstructor = this.switchInstructor.bind(this);

        this.state = {
            isLoading: true,
            loginInfo: null
        };
    }

    async componentDidMount() {
        // Set up globally-accessible functions
        GlobalStateService.setValue('logOut', async () => {
            this.setState({ isLoading: true });
            await AccountService.logOut();
            this.props.history.replace('/');
            this.checkLogin();
        });
        GlobalStateService.setValue('switchInstructor', this.switchInstructor);

        // Add global scroll wheel override for number fields
        document.addEventListener('wheel', () => {
            if (document.activeElement.type == 'number') {
                document.activeElement.blur();
            }
        });

        // Check login
        this.checkLogin();
    }

    checkLogin() {
        return new Promise(async (resolve, reject) => {

            // Determine if logged in
            let loginInfo;
            try {
                loginInfo = await AccountService.getLoginInfo();

                loginInfo.isInstructor = (loginInfo.user.role == 'Instructor' || loginInfo.user.role == 'InstructorManager');
                loginInfo.isManager = (loginInfo.user.role == 'Manager' || loginInfo.user.role == 'InstructorManager');
                loginInfo.isAdmin = (loginInfo.user.role == 'Admin');
                loginInfo.isVendor = (loginInfo.user.role == 'Vendor');
                loginInfo.isAccountant = (loginInfo.user.role == 'Accountant');

                loginInfo.isVendorOrAdmin = loginInfo.isAdmin || loginInfo.isVendor;
                loginInfo.isAccountantOrAdmin = loginInfo.isAdmin || loginInfo.isAccountant;
                loginInfo.isVendorOrAccountantOrAdmin = loginInfo.isVendor || loginInfo.isAdmin || loginInfo.isAccountant;
                loginInfo.isSiteAdmin = loginInfo.user.isSiteAdmin;
                loginInfo.isSuperAdmin = loginInfo.user.isSuperAdmin;
                loginInfo.isAccountantOrSiteAdmin = (loginInfo.isAdmin && loginInfo.user.isSiteAdmin) || loginInfo.isAccountant;
                loginInfo.isManagerOrAdmin = (loginInfo.isManager || loginInfo.isAdmin);
                loginInfo.isInstructorOrManager = (loginInfo.isInstructor || loginInfo.isManager);

                // Update body
                if (loginInfo.site.isDevTest) {
                    document.querySelector('body').classList.add('is-dev-test');
                }
            } catch (e) {
                // Ignore error - loginInfo will be null
            }

            // If we landed via candidate-login URL but we're already logged in, go to the correct new URL
            if (loginInfo) {
                const pathName = this.props.history.location.pathname;
                const match = pathName.match(/candidate-login\/(.+)\/(.+)/i);
                if (match && match[2]) {
                    this.props.history.replace('/' + match[2])
                }
            }

            // Update screen
            GlobalStateService.setValue('loginInfo', loginInfo);
            this.setState({
                loginInfo,
                isLoading: false
            }, () => resolve(loginInfo));
        });
    }

    async switchInstructor(id) {
        await AccountService.switchInstructor(id);
        const loginInfo = await this.checkLogin();
        this.props.history.replace('/instructor/' + loginInfo.instructor.slug);
    }
    
    //-------------------------------------------------------------------------------------------------------------------

    render() {
        const {
            isLoading,
            loginInfo
        } = this.state;

        // Loading login status
        if (isLoading) {
            return (<Loader />);
        }

        // Not logged in yet
        if (!loginInfo || !loginInfo.user) {
            return (
                <div className="login-page">
                    <Switch>
                        <Route
                            path="/password-reset/:token?/:email?"
                            render={(props) =>
                                <PasswordResetPage
                                    onComplete={e => {
                                        this.setState({ isLoading: true });
                                        this.props.history.replace('/');
                                        this.checkLogin();
                                    }}
                                    {...props}
                                />
                            }
                        />
                        <Route
                            path="/candidate-login/:guid"
                            render={(props) =>
                                <LoginByGUID
                                    guid={props.match.params.guid}
                                    onLogIn={() => this.checkLogin()}
                                />
                            }
                        />
                        <LoginPage
                            onLogIn={this.checkLogin}
                        />
                    </Switch>
                </div>
            );
        }
        
        // Logged in but onboarding
        switch (loginInfo.onboardingStatus) {
            case 'Questionnaire':
            case 'AwaitQuestionnaireApproval':
            case 'AwaitInterview':
            case 'SignContract':
                return (
                    <Layout hideMenu>
                        <Switch>
                            <Route path="/sign-contract" component={SignContractPage} />
                            <Route>
                                <OnboardingPage
                                    isAwaitingApproval={loginInfo.onboardingStatus == 'AwaitQuestionnaireApproval'}
                                />
                            </Route>
                        </Switch>
                    </Layout>
                );
            case 'AwaitSubAgencySignContract':
                return (
                    <Layout hideMenu>
                        <Container tag="main" className="contract-not-signed">
                            <section>
                                <h2>Please wait for your company to sign the contract</h2>
                                <p>We are currently waiting for your company to sign the contract. You will be notified once this has been done.</p>
                            </section>
                        </Container>
                    </Layout>
                );
            case 'NotInvited':
                return (
                    <Layout hideMenu>
                        <Container tag="main" className="candidate-not-invited">
                            <section>
                                <h2>Please wait for your invitation email</h2>
                                <p>Our records show that you have not been sent the invitation email yet.</p>
                                <p>If this is in error, please contact us.</p>
                            </section>
                        </Container>
                    </Layout>
                );
        }

        // Logged in as normal
        return (
            <Layout>
                <Switch>
                    <Route path="/instructor/:slug/:tab?" render={(props) =>
                        <InstructorViewPage {...props} />
                    } />
                    <Route path="/availability-selector/:slug?" component={AvailabilitySelectorPage} />
                    <Route path="/invoicing/my-invoices" component={InstructorInvoicesPage} />
                    <Route path="/sign-contract" component={SignContractPage} />
                    <Route path="/help/:slug?" component={HelpPage} />

                    {/* Admin only */}
                    {loginInfo.isAdmin && <Route path="/enquiries" component={EnquiriesPage} />}
                    {loginInfo.isAdmin && <Route path="/course-checker" component={CourseCheckerPage} />}
                    {loginInfo.isAdmin && <Route path="/swap-search" component={JobRequestsPage} />}
                    {loginInfo.isAdmin && <Route path="/courses" component={CoursesPage} />}
                    {loginInfo.isAdmin && <Route path="/email-types" component={EmailTypesPage} />}
                    {loginInfo.isAdmin && <Route path="/partners" component={PartnersPage} />}
                    {loginInfo.isAdmin && <Route path="/cost-centres" component={CostCentresPage} />}
                    {loginInfo.isAdmin && <Route path="/sub-agencies" component={SubAgencyListPage} />}
                    {loginInfo.isAdmin && <Route path="/sub-agency/:slug?/:tab?" component={SubAgencyViewPage} />}
                    {loginInfo.isAdmin && <Route path="/unbilled-days" component={UnbilledDaysPage} />}
                    {loginInfo.isAdmin && <Route path="/users/:role?" component={UsersPage} />}
                    {loginInfo.isAdmin && <Route path="/certs" component={CertsPage} />}
                    {loginInfo.isAdmin && <Route path="/import" component={ImportPage} />}
                    {loginInfo.isAdmin && <Route path="/referral-codes" component={ReferralCodesPage} />}
                    {loginInfo.isAdmin && <Route path="/enablement-compare" component={EnablementComparePage} />}
                    {loginInfo.isAdmin && <Route path="/imported-bookings" component={ImportedBookingsPage} />}
                    {loginInfo.isAdmin && <Route path="/contacts/:role?" component={ContactsPage} />}
                    {loginInfo.isAdmin && <Route path="/contracts" component={ContractsPage} />}
                    {loginInfo.isAdmin && <Route path="/dev-tasks" component={DevTasksPage} />}
                    {loginInfo.isAdmin && <Route path="/countries" component={CountriesPage} />}
                    {loginInfo.isAdmin && <Route path="/help-articles" component={HelpArticlesPage} />}
                    {loginInfo.isAdmin && <Route path="/tags" component={TagsPage} />}

                    {/* Accountant or admin */}
                    {loginInfo.isAccountantOrAdmin && <Route path="/invoicing/invoices" component={InvoicesPage} />}
                    {loginInfo.isAccountantOrAdmin && <Route path="/invoicing/bills" component={BillsPage} />}

                    {/* Vendor or Admin only */}
                    {loginInfo.isVendorOrAdmin &&
                        <Route path="/instructors/:type" render={(props) =>
                            <InstructorListPage {...props} />
                        }
                    />}
                    {loginInfo.isVendorOrAccountantOrAdmin && <Route path="/bookings/:page?" component={BookingsPage} />}
                    {loginInfo.isVendorOrAdmin && <Route path="/calendar" component={CalendarPage} />}
                    {loginInfo.isVendorOrAdmin && <Route path="/enablement" component={EnablementPage} />}
                    {loginInfo.isVendorOrAdmin && <Route path="/reports/:slug?" component={ReportsPage} />}
                    {loginInfo.isVendorOrAdmin && <Route path="/map" component={MapPage} />}
                    {loginInfo.isVendorOrAdmin && <Route path="/todos/:id?" component={TodosPage} />}
                    {loginInfo.isVendorOrAdmin && <Route path="/" exact component={DashboardPage} />}
                    {loginInfo.isInstructorOrManager && loginInfo.instructor &&
                        <Redirect to={`/instructor/${loginInfo.instructor.slug}`} />
                    }
                    {loginInfo.isVendorOrAdmin &&
                        <Redirect to="/instructors/approved" />
                    }

                </Switch>
            </Layout>
        );
    }
}

export default withRouter(App);
